import React, {
  useCallback,
  useEffect, useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import MaiaIconImage from '@/media/MaiaIA/iconImage.png';
import IconAnimation from '@/components/BannerAI/media/AiAnimation.webp';
import listeningImage from '@/media/MaiaIA/listening.png';
import * as Styles from './NewAiHeaderButton.styles';
import {
  IconStarMoving, IconAudioRecord, IconArrow, Button,
  IconClose,
} from 'gatorcomponents';
import { OPTIONS } from './NewAiHeaderButton.types';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { AudioRecording } from '../../AiChat/components/AudioRecording';
import { useDispatch, useSelector } from 'react-redux';
import { useAiChat } from '@/hooks/AiChat';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { ATIVACAO_N8N_INHOUSE_URLS } from '@/config/GrowthBook/constants';
import { WSS_ROUTES } from '../../AiChat/AiChat.enum';
import { merlinActions } from '@/redux/modules';
import { useResize } from '@/hooks/useResize';
import useLocale from '@/hooks/useLocale';
import { useChatbot } from '@/contexts/Chatbot';

const NewAiHeaderButton = ({
  hideOnMobile,
  hideOnDesktop,
  overlayZIndex,
  onFocusChange,
}) => {
  const dispatch = useDispatch();
  const chatbot = useChatbot();
  const { triggerExternalMessage, triggerExternalAudioBlob } = useAiChat();
  const fullScreenAudio = false;
  const { recording, handleStartRecording, handleStopRecording } = useAudioRecorder({
    onStop: audioBlob => triggerExternalAudioBlob(audioBlob, fullScreenAudio),
  });
  const { aichat: generalChatLocale } = useLocale();
  const { headerLocale } = generalChatLocale;
  const view = useResize();
  const isMobile = view.width < 960;

  const [iconImage, setIconImage] = useState(MaiaIconImage || IconAnimation);
  const [focused, setFocused] = useState(false);
  const [moreThanOneLine, setMoreThanOneLine] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [typedMessage, setTypedMessage] = useState('');

  const { loadingTranscription } = useSelector(state => state.merlin);
  const wssToggleRoutes = useFeatureValue(ATIVACAO_N8N_INHOUSE_URLS);

  const inputRef = useRef(null);
  const inputFieldRef = useRef(null);
  const isRecording = recording || loadingTranscription;
  const isChatbotLoading = chatbot.isActive && !chatbot.isReady;

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    if (!chatbot.isActive) {
      const route = wssToggleRoutes.general || WSS_ROUTES.GENERAL;
      dispatch(merlinActions.inhouse.setRoute(route));
    } else {
      chatbot.setRoute('general');
    }
    triggerExternalMessage({ message: typedMessage, fullScreen: false });
    setTypedMessage('');
  }, [typedMessage, dispatch, triggerExternalMessage, wssToggleRoutes, chatbot]);

  const updateImage = useCallback(() => {
    const eightSeconds = 8000;
    setTimeout(() => {
      setIconImage(MaiaIconImage);
    }, eightSeconds);
  }, []);

  useEffect(() => {
    updateImage();
  }, [updateImage]);


  const handleFocus = () => {
    const newFocusedState = !focused;
    setFocused(newFocusedState);
    if (onFocusChange) {
      onFocusChange(newFocusedState);
    }
  };

  const keyListener = useCallback((event) => {
    if (event.key === 'Escape') {
      setFocused(false);
      if (inputFieldRef.current) {
        inputFieldRef.current.blur();
      }
    }

    if (event.key === 'Enter' && typedMessage) {
      handleSubmit(event);
    }
  }, [typedMessage, handleSubmit]);

  useEffect(() => {
    document.addEventListener('keydown', keyListener);
    return () => {
      document.removeEventListener('keydown', keyListener);
    };
  }, [keyListener]);

  const hideBodyOverflow = useCallback(() => {
    if (focused) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [focused]);

  useEffect(() => {
    hideBodyOverflow();

    return () => {
      hideBodyOverflow();
    };
  }, [hideBodyOverflow]);

  const handleOptionAction = ({ value, message }) => {
    if (value === OPTIONS.MORE) {
      setShowMoreOptions(prev => !prev);
      return;
    }

    const handleRoute = () => {
      if (!chatbot.isActive) {
        const routes = {
          [OPTIONS.CREATE_EMAIL]: wssToggleRoutes.email || WSS_ROUTES.EMAIL,
          [OPTIONS.CONFIGURE_DOMAIN]: wssToggleRoutes.domain || WSS_ROUTES.DOMAIN,
          [OPTIONS.CREATE_SITE]: wssToggleRoutes.site || WSS_ROUTES.SITE,
          general: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
        };
        dispatch(merlinActions.inhouse.setRoute(routes[value] || routes.general));
      } else {
        const routes = {
          [OPTIONS.CREATE_EMAIL]: 'email',
          [OPTIONS.CONFIGURE_DOMAIN]: 'domain',
          [OPTIONS.CREATE_SITE]: 'site',
          general: 'general',
        };
        chatbot.setRoute(routes[value] || routes.general);
      }
    };


    const handleSelection = () => {
      handleRoute();
      triggerExternalMessage({ message, fullScreen: false });
    };

    return handleSelection();
  };

  const getTextWidth = (text, font) => {
    const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
    const context = canvas.getContext('2d');
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  };

  const handleInput = (text) => {
    const wrapper = inputRef.current;
    const textArea = inputFieldRef.current;
    if (!textArea) return;


    const style = getComputedStyle(inputFieldRef.current);
    const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

    const longestLine = text.split('\n').reduce((a, b) => (a.length > b.length ? a : b), '');

    const textWidth = Math.round(getTextWidth(longestLine, font));
    const defaultHeight = 24;
    const maxHeight = 200;
    const imageWidth = 32;
    const actionsWidth = 159;
    const tolerance = 45;

    const wrapperWithTolerance = wrapper.offsetWidth - imageWidth - actionsWidth - tolerance;

    textArea.style.height = `${defaultHeight}px`;
    if (textWidth < wrapperWithTolerance) {
      textArea.style.width = `calc(100% - ${imageWidth}px - ${actionsWidth}px)`;
      setMoreThanOneLine(false);
    }

    if (textWidth >= wrapperWithTolerance) {
      textArea.style.width = `calc(100% - ${imageWidth}px)`;
      const newHeight = Math.min(inputFieldRef.current.scrollHeight, maxHeight);
      textArea.style.height = `${newHeight >= maxHeight ? maxHeight : textArea.scrollHeight}px`;
      if (newHeight >= maxHeight) {
        textArea.style.overflow = 'auto';
      }
      setMoreThanOneLine(true);
    }
  };

  const handleOnChange = (e) => {
    setFocused(true);
    setTypedMessage(e.target.value);
    handleInput(e.target.value);
  };

  if (hideOnMobile && isMobile) {
    return null;
  }

  if (hideOnDesktop && !isMobile) {
    return null;
  }

  return (
    <>
      {((focused || isRecording) && !hideOnDesktop) && createPortal(
        <Styles.Overlay onClick={handleFocus} $zIndex={overlayZIndex} />,
        document.getElementById('navMenu') || document.body,
      )}

      {(focused || isRecording) && (
        <Styles.Overlay onClick={handleFocus} id="bodyOverlay" $zIndex={overlayZIndex} />
      )}

      <Styles.Wrapper $focused={focused}>
        <Styles.InputBorder $focused={focused} ref={inputRef} $moreThanOneLine={moreThanOneLine}>
          <Styles.InputWrapper $moreThanOneLine={moreThanOneLine}>
            <Styles.Image src={isRecording ? listeningImage : iconImage} />

            {!isRecording && (
            <>
              <Styles.Input
                ref={inputFieldRef}
                onFocus={handleFocus}
                placeholder={headerLocale.talkToMaiaMobile}
                onChange={e => handleOnChange(e)}
                disabled={isChatbotLoading}
              />

              <Styles.ActionsWrapper>
                <Styles.ActionsButton
                  $focused={focused}
                  onClick={handleFocus}
                  $disabled={isChatbotLoading}
                >
                  <IconStarMoving color={focused ? 'primaryMedium' : 'lowDark'} />
                  {headerLocale.actions}
                </Styles.ActionsButton>

                <Styles.ActionsButton onClick={handleStartRecording} $disabled={isChatbotLoading}>
                  <IconAudioRecord />
                </Styles.ActionsButton>
              </Styles.ActionsWrapper>

              {typedMessage && (
                <Styles.SubmitWrapper>
                  <Button
                    type="submit"
                    variant="primary"
                    width="fitContent"
                    label={<IconArrow direction="right" />}
                    testId="submit"
                    onClick={handleSubmit}
                  />
                </Styles.SubmitWrapper>
              )}
            </>
            )}

            {isRecording && (
              <Styles.SubmitWrapper>
                <AudioRecording
                  onCancel={() => handleStopRecording({ cancelRecording: true })}
                  onSend={() => handleStopRecording({ cancelRecording: false })}
                  isRecording={recording}
                  transcribing={loadingTranscription}
                  locales={{
                    recordingLabel: headerLocale.recordingLabel,
                    transcribingLabel: headerLocale.transcribingLabel,
                  }}
                  whiteBackground
                />
              </Styles.SubmitWrapper>
            )}

          </Styles.InputWrapper>
        </Styles.InputBorder>

        {(focused && !typedMessage.length) && (
          <Styles.OptionsWrapper>
            {headerLocale.options.map(option => (!option.showMoreOptions || (option.showMoreOptions && !!showMoreOptions)) && (
              <Styles.Option
                key={option.label}
                role="button"
                onClick={() => handleOptionAction({ value: option.value, message: option.message })}
              >
                {option.Icon && <option.Icon size="sm2" />}
                {option.label}
              </Styles.Option>
            ))}
          </Styles.OptionsWrapper>
        )}

        {focused && (
          <Styles.CloseButtonWrapper>
            <Button
              variant="secondary"
              label={<IconClose size="md" />}
              onClick={handleFocus}
              width="fitContent"
            />
          </Styles.CloseButtonWrapper>
        )}
      </Styles.Wrapper>
    </>
  );
};

export default NewAiHeaderButton;
